<template>
  <div class="laowai-page">
    <section class="laowai-hero">
      <img
        :src="require('@/assets/laowai-survival/feature-graphic.png')"
        :alt="t.laowai_hero_alt"
        class="laowai-hero-img"
        loading="eager" />
      <div class="laowai-hero-overlay">
        <img
          :src="require('@/assets/laowai-survival/icon.png')"
          :alt="t.laowai_icon_alt"
          class="laowai-icon"
          loading="eager" />
        <span class="laowai-studio">Club Baby Pro</span>
        <h1 class="laowai-title">Laowai Survival</h1>
        <p class="laowai-subtitle">{{ t.laowai_subtitle }}</p>
      </div>
    </section>

    <section class="laowai-intro">
      <b-container>
        <p class="laowai-pitch" v-reveal>{{ t.laowai_pitch }}</p>
        <ul class="laowai-facts" v-reveal>
          <li><strong>Android</strong><span>{{ t.laowai_fact_android }}</span></li>
          <li><strong>{{ t.laowai_fact_genre }}</strong><span>{{ t.laowai_fact_choices }}</span></li>
          <li><strong>{{ t.laowai_fact_languages }}</strong><span>{{ t.laowai_fact_localized }}</span></li>
          <li><strong>45</strong><span>{{ t.laowai_fact_events }}</span></li>
        </ul>
      </b-container>
    </section>

    <section class="section">
      <b-container>
        <div class="laowai-feature" v-reveal>
          <div class="laowai-shot-wrap">
            <img
              :src="require('@/assets/laowai-survival/screenshot-3.png')"
              :alt="t.laowai_choice_alt"
              class="laowai-shot"
              loading="lazy" />
          </div>
          <div class="laowai-feature-text">
            <span class="eyebrow">{{ t.laowai_choices_eyebrow }}</span>
            <h2 class="laowai-section-title">{{ t.laowai_choices_title }}</h2>
            <p>{{ t.laowai_choices_text }}</p>
          </div>
        </div>
      </b-container>
    </section>

    <section class="section laowai-band">
      <b-container>
        <div class="laowai-feature laowai-feature-reverse" v-reveal>
          <div class="laowai-feature-text">
            <span class="eyebrow">{{ t.laowai_modes_eyebrow }}</span>
            <h2 class="laowai-section-title">{{ t.laowai_modes_title }}</h2>
            <div class="mode-list">
              <div>
                <h3>{{ t.laowai_classic_title }}</h3>
                <p>{{ t.laowai_classic_text }}</p>
              </div>
              <div>
                <h3>{{ t.laowai_endless_title }}</h3>
                <p>{{ t.laowai_endless_text }}</p>
              </div>
            </div>
          </div>
          <div class="laowai-shot-pair">
            <img
              :src="require('@/assets/laowai-survival/screenshot-1.png')"
              :alt="t.laowai_menu_alt"
              class="laowai-shot"
              loading="lazy" />
            <img
              :src="require('@/assets/laowai-survival/screenshot-2.png')"
              :alt="t.laowai_result_alt"
              class="laowai-shot"
              loading="lazy" />
          </div>
        </div>
      </b-container>
    </section>

    <section class="section">
      <b-container>
        <div class="laowai-content card-cbp" v-reveal>
          <span class="eyebrow">{{ t.laowai_content_eyebrow }}</span>
          <h2 class="laowai-section-title">{{ t.laowai_content_title }}</h2>
          <p>{{ t.laowai_content_text }}</p>
        </div>
      </b-container>
    </section>

    <section class="section laowai-services-section">
      <b-container>
        <div class="laowai-services" v-reveal>
          <h2 class="laowai-services-title">{{ t.laowai_services_title }}</h2>
          <ul class="laowai-services-list">
            <li v-for="item in t.laowai_services_items" :key="item">{{ item }}</li>
          </ul>
          <p>{{ t.laowai_services_note }}</p>
        </div>
      </b-container>
    </section>

    <section class="laowai-actions">
      <b-container>
        <div class="laowai-actions-inner" v-reveal>
          <p class="laowai-testing">{{ t.laowai_internal_testing }}</p>
          <div class="laowai-buttons">
            <a
              class="btn-cbp"
              href="https://github.com/pbarillec/laowai-survival-cocos"
              target="_blank"
              rel="noopener noreferrer">
              <font-awesome-icon :icon="['fab', 'github']" />
              GitHub
            </a>
            <a class="btn-quiet" href="/laowai-survival/privacy-policy.html">
              {{ t.laowai_privacy_link }}
            </a>
          </div>
        </div>
      </b-container>
    </section>
  </div>
</template>

<script>
import en from '@/en'
import fr from '@/fr'

export default {
  name: 'LaowaiSurvivalGame',
  data () {
    return {
      languages: {
        en,
        fr
      }
    }
  },
  computed: {
    language () {
      return this.$store.state.language
    },
    t () {
      return this.languages[this.language]
    }
  }
}
</script>

<style scoped lang="scss">
  .laowai-page {
    --laowai-warm: #f59e0b;
    --laowai-brown: #4b2d20;
    --laowai-coral: #ef5a42;
  }

  .laowai-hero {
    position: relative;
    min-height: clamp(360px, 58vw, 640px);
    overflow: hidden;
    border-bottom: 1px solid var(--border);
    background: var(--laowai-warm);
  }

  .laowai-hero-img {
    display: block;
    width: 100%;
    height: clamp(360px, 58vw, 640px);
    object-fit: cover;
  }

  .laowai-hero-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: clamp(1.5rem, 5vw, 4rem) 1rem;
    text-align: center;
    color: #fff;
    background: linear-gradient(to top, rgba(47, 28, 18, 0.9) 0%, rgba(47, 28, 18, 0.35) 42%, rgba(47, 28, 18, 0.04) 100%);
  }

  .laowai-icon {
    width: clamp(72px, 12vw, 120px);
    height: clamp(72px, 12vw, 120px);
    object-fit: cover;
    border-radius: 24%;
    border: 3px solid rgba(255, 255, 255, 0.88);
    box-shadow: 0 18px 35px rgba(47, 28, 18, 0.35);
    margin-bottom: 0.9rem;
  }

  .laowai-studio {
    font-family: 'agency', sans-serif;
    text-transform: uppercase;
    letter-spacing: 5px;
    color: #facc15;
    font-size: clamp(0.85rem, 2vw, 1.2rem);
  }

  .laowai-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(2.7rem, 9vw, 6.2rem);
    margin: 0.15rem 0 0.6rem;
    text-shadow: 0 5px 30px rgba(0, 0, 0, 0.55);
  }

  .laowai-subtitle {
    max-width: 48ch;
    margin: 0;
    font-family: 'agency', sans-serif;
    font-size: clamp(1.1rem, 2.4vw, 1.45rem);
  }

  .laowai-intro {
    padding: clamp(2.5rem, 6vw, 4.5rem) 0 0;
    text-align: center;
  }

  .laowai-pitch {
    max-width: 64ch;
    margin: 0 auto clamp(2rem, 4vw, 3rem);
    font-size: clamp(1.1rem, 2vw, 1.4rem);
    color: var(--text);
    font-family: 'agency', sans-serif;
  }

  .laowai-facts {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: clamp(1rem, 3vw, 2.5rem);
  }

  .laowai-facts li {
    display: flex;
    flex-direction: column;
    min-width: 120px;
  }

  .laowai-facts strong {
    font-family: 'agency', sans-serif;
    font-size: clamp(1.35rem, 3vw, 2rem);
    color: var(--laowai-coral);
    line-height: 1.1;
  }

  .laowai-facts span {
    font-size: 0.85rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .laowai-feature {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    align-items: center;
    gap: clamp(2rem, 5vw, 4.5rem);
  }

  .laowai-feature-reverse {
    grid-template-columns: 1.05fr 0.95fr;
  }

  .laowai-band {
    background: linear-gradient(180deg, transparent, rgba(245, 158, 11, 0.08), transparent);
  }

  .laowai-feature-text {
    text-align: left;
  }

  .laowai-feature-text p,
  .mode-list p,
  .laowai-content p {
    color: var(--text-muted);
    font-size: clamp(1rem, 1.5vw, 1.15rem);
  }

  .laowai-section-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(2.3rem, 5.8vw, 4rem);
    margin-bottom: 1rem;
  }

  .laowai-shot-wrap,
  .laowai-shot-pair {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }

  .laowai-shot-pair {
    align-items: flex-start;
  }

  .laowai-shot {
    width: min(100%, 320px);
    border-radius: var(--radius);
    border: 1px solid rgba(75, 45, 32, 0.18);
    box-shadow: 0 22px 45px -22px rgba(75, 45, 32, 0.55);
    background: var(--laowai-brown);
  }

  .laowai-shot-pair .laowai-shot {
    width: min(48%, 260px);
  }

  .mode-list {
    display: grid;
    gap: 1rem;
  }

  .mode-list h3 {
    font-size: 1.5rem;
    color: var(--laowai-coral);
    margin-bottom: 0.25rem;
  }

  .laowai-content {
    max-width: 820px;
    margin: 0 auto;
    padding: clamp(2rem, 5vw, 3.5rem);
    text-align: center;
  }

  .laowai-services-section {
    padding-top: 0;
  }

  .laowai-services {
    max-width: 820px;
    margin: 0 auto;
    text-align: center;
  }

  .laowai-services-title {
    font-size: 1.2rem;
    color: var(--laowai-coral);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 1rem;
  }

  .laowai-services-list {
    list-style: none;
    padding: 0;
    margin: 0 auto 1rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
  }

  .laowai-services-list li {
    border: 1px solid rgba(75, 45, 32, 0.18);
    border-radius: 999px;
    padding: 0.55rem 0.9rem;
    color: var(--text-muted);
    background: rgba(255, 255, 255, 0.55);
    font-size: 0.95rem;
  }

  .laowai-services p {
    max-width: 62ch;
    margin: 0 auto;
    color: var(--text-muted);
  }

  .laowai-actions {
    padding: clamp(2.5rem, 6vw, 5rem) 0;
    text-align: center;
  }

  .laowai-actions-inner {
    max-width: 760px;
    margin: 0 auto;
  }

  .laowai-testing {
    color: var(--text-muted);
    margin-bottom: 1.5rem;
  }

  .laowai-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
  }

  .btn-quiet {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.85rem 1.5rem;
    border-radius: 999px;
    border: 1px solid var(--border-strong);
    color: var(--text);
    text-decoration: none;
    font-family: 'agency', sans-serif;
    font-size: 1.1rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }

  .btn-quiet:hover,
  .btn-quiet:focus {
    border-color: var(--laowai-coral);
    background: rgba(245, 158, 11, 0.10);
    color: var(--text);
    text-decoration: none;
    transform: translateY(-2px);
  }

  @media (max-width: 850px) {
    .laowai-feature,
    .laowai-feature-reverse {
      grid-template-columns: 1fr;
      text-align: center;
    }

    .laowai-feature-text {
      text-align: center;
    }

    .laowai-feature .laowai-shot-wrap {
      order: -1;
    }

    .laowai-shot {
      width: min(100%, 300px);
    }
  }

  @media (max-width: 575px) {
    .laowai-hero,
    .laowai-hero-img {
      min-height: 420px;
      height: 420px;
    }

    .laowai-hero-img {
      object-position: 57% center;
    }

    .laowai-shot-pair {
      flex-direction: column;
      align-items: center;
    }

    .laowai-shot-pair .laowai-shot {
      width: min(100%, 300px);
    }
  }
</style>
